import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Chip, InfoTip } from '@zarya/ui';
import { errorSpec } from '@app/errors';
import { identityAccessHref, ROUTE_HUMAN_ACCESS } from '@app/navigation';
import { ReadingNote } from '@app/reading';
import { DeclarationNotice } from '@app/main/Runtime/DeclarationNotice';
import { useActivePoliciesCiting } from './Contracts.api';
import { pdpLabel, securityReadyLabel, type ContractView } from './ContractsModel';
import { t, tn } from '@app/i18n';
import s from './Contracts.module.css';

/**
 * Card **Estado** (FR-070, FR-088): SecurityReady, pendências e a publicação no
 * PDP — **em dia / divergente / não lido**, três estados.
 *
 * "não lido" não é "divergente": quando a leitura do PDP falha, a tela diz que
 * não sabe, em vez de afirmar que está errado.
 */
export function ContractStateCard({
  view,
  readingPending = false,
}: {
  view: ContractView;
  /** A tela pediu a leitura nova do PDP e o pedido não voltou: a linha já diz «lendo». */
  readingPending?: boolean;
}) {
  const navigate = useNavigate();
  const estado = view.state;
  const ready = securityReadyLabel(estado.security_ready);
  const pdp = pdpLabel(estado);
  const politicas = view.granting_policies;
  const identidade = view.identity;
  /**
   * `granting_policies` lista as regras de Human Access que ALCANÇAM esta
   * identidade: as ativas que a cobrem (lista ou «todas») e concedem alguma
   * tool que o contrato expõe. `tightens_contract` marca a que recorta teto.
   *
   * O QUE MUDOU EM 04/09
   *
   *   Até aqui a lista trazia só a regra cujo limite era mais **apertado** que o
   *   do contrato. No modelo da interseção isso escondia o essencial: a regra
   *   humana não alarga a identidade, mas é ela que AFIRMA o eixo pessoa — sem
   *   uma regra ativa cobrindo e concedendo, o PDP nega. Uma regra sem limite
   *   não «muda nada» pelo critério antigo e é, ao mesmo tempo, a razão de o
   *   acesso existir; a tela dizia «nenhuma» sobre agentes que o PDP autoriza.
   *
   *   Então recortar teto virou ATRIBUTO da linha (o chip «recorta teto»), e não
   *   critério de entrada.
   *
   * A LINHA DE BAIXO SOBREVIVE, COM OUTRO SENTIDO
   *
   *   `citando` (todas as regras ativas que citam a identidade) ainda pega o que
   *   `granting_policies` não pega: a regra que cita a identidade e concede
   *   **só** tools fora do contrato. Ela não é via de acesso nenhuma — e dizer
   *   isso é diferente de omiti-la. Era «cita sem mudar o contrato» (ajustes-3,
   *   28/08, item 11.4); agora é «cita sem conceder nada que o contrato exponha».
   */
  const citando = useActivePoliciesCiting(identidade.id);
  const iDsThatGrant = new Set(politicas.map((p) => p.policy_id));
  const withoutExcedente = (citando.data ?? []).filter((p) => !iDsThatGrant.has(p.id));

  return (
    <Card title={t('contracts.state.title')}>
      <dl className={s.dl}>
        <dt>{t('contracts.state.securityReady')}</dt>
        <dd>
          <Badge tone={ready.tone} tip={t('contracts.state.securityReadyTip')}>
            {ready.rotulo}
          </Badge>
        </dd>

        <dt>{t('contracts.state.pendencies')}</dt>
        <dd>
          {estado.pending_items ?? 0}
          {(estado.pending_items ?? 0) > 0 ? (
            <>
              {' '}
              <Button
                variant="soft"
                size="sm"
                onClick={() => navigate(`/aprovacoes?identidade=${identidade.id}`)}
              >
                {t('contracts.state.seeQueue')}
              </Button>
            </>
          ) : null}
        </dd>

        <dt>{t('contracts.state.publishedInPdp')}</dt>
        <dd>
          <Badge tone={pdp.tone}>{pdp.rotulo}</Badge>
          {/* De quando é a foto do L8 (aim-6ph.13, §4): «em dia · lido às 14:05». Com a falha da primeira leitura a
              linha já diz «não foi possível ler», e a hora não entra: não há foto. */}
          {estado.error_code ? null : (
            <>
              {' '}
              <ReadingNote
                reading={estado.pdp_reading}
                family="pdp"
                pending={readingPending}
                compose={(reading) => `· ${reading}`}
                className={s.muted}
                testId="pdp-reading"
              />
            </>
          )}
          {estado.error_code ? (
            <span className={s.detalhePdp}>
              {errorSpec(estado.error_code).mensagem} ({estado.error_code})
            </span>
          ) : estado.pdp_detail ? (
            <span className={s.detalhePdp}>{estado.pdp_detail}</span>
          ) : null}
        </dd>

        <dt>
          {t('contracts.state.humanAccessRules')}
          <InfoTip
            text={t('contracts.state.humanAccessTip')}
            label={t('contracts.state.humanAccessTipLabel')}
            size="xs"
          />
        </dt>
        <dd>
          {politicas.length ? (
            <span className={s.chips}>
              {politicas.map((politica) => (
                <Chip key={politica.policy_id ?? politica.name}>
                  {politica.name}
                  {/* Só na que recorta: um marcador em toda linha não marca nada. */}
                  {politica.tightens_contract ? t('contracts.block.tightensCap') : ''}
                </Chip>
              ))}
            </span>
          ) : estado.error_code ? (
            // `granting_policies` é `required` array sem `null` no contrato,
            // então vem `[]` **nos dois casos**: "li e nenhuma regra alcança" e
            // "não consegui ler". Quem separa é o `erro_codigo` do estado. Dizer
            // "nenhuma" quando a leitura falhou afirmaria que nenhuma regra
            // alcança a identidade — hoje isso significa "o PDP nega tudo", que
            // é uma afirmação forte sobre algo que ninguém leu.
            // Frase própria da linha, e não a mesma do PDP acima: repetir
            // «{mensagem} ({código})» duas vezes no mesmo card é ruído, e o que
            // muda aqui é *o que* ficou desconhecido — a lista de regras.
            <span className={s.detalhePdp}>
              {t('contracts.state.pdpReadFailed', { code: estado.error_code })}
            </span>
          ) : (
            <span className={s.muted}>{t('contracts.state.rulesEmpty')}</span>
          )}
          {withoutExcedente.length > 0 && !estado.error_code ? (
            <span className={s.detalhePdp}>
              {/* `tn` no lugar do `=== 1` escrito à mão: a regra de plural do
                  idioma sai do catálogo, e o número vem formatado pelo locale. */}
              {tn('contracts.state.rulesWithoutCapability', withoutExcedente.length)}
              {withoutExcedente.map((p) => p.nome).join(', ')}
            </span>
          ) : null}
        </dd>

        <dt>{t('contracts.field.approvedBy')}</dt>
        <dd>{view.in_force?.approved_by ?? <span className={s.muted}>—</span>}</dd>
      </dl>

      {/* aim-ypw: a mesma linha do card Security Ready de T15, do mesmo dado do servidor. */}
      <DeclarationNotice difference={estado.declaration_difference} identityId={identidade.id} />

      <div className={s.acoes}>
        <Button
          variant="out"
          size="sm"
          icon="key"
          onClick={() => navigate(identityAccessHref(identidade.id))}
        >
          {t('contracts.state.openIdentityAccess')}
        </Button>
        <Button
          variant="out"
          size="sm"
          icon="shield"
          onClick={() => navigate(ROUTE_HUMAN_ACCESS)}
        >
          {t('contracts.state.openHumanAccess')}
        </Button>
        <Button variant="out" size="sm" icon="broadcast" onClick={() => navigate('/runtime')}>
          {t('contracts.state.openRuntime')}
        </Button>
        <Button variant="out" size="sm" icon="play" onClick={() => navigate('/simulador')}>
          {t('contracts.state.simulate')}
        </Button>
      </div>
    </Card>
  );
}
